import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, useCallback, useId } from 'react';
import { Check, CircleAlert, Info, TriangleAlert, X } from 'lucide-react';
import '../pages/warehouses/warehouses.css';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [notifications, setNotifications] = useState(new Map());
  const update = useCallback((id, notification) => {
    setNotifications(current => {
      const next = new Map(current);
      if (notification) next.set(id, notification);
      else next.delete(id);
      return next;
    });
  }, []);
  return <ToastContext.Provider value={update}>
    {children}
    <div className="wh-toast-container" aria-label="Notifications">
      {[...notifications].map(([id, notification]) => <ToastNotification key={id} {...notification} />)}
    </div>
  </ToastContext.Provider>;
}

export default function Toast({ toast, onClose }) {
  const update = useContext(ToastContext);
  const id = useId();
  const close = useRef(onClose);
  close.current = onClose;
  useLayoutEffect(() => {
    if (!update) return undefined;
    const notificationId = id;
    update(notificationId, toast ? { toast, onClose: () => close.current?.() } : null);
    return () => update(notificationId, null);
  }, [update, toast, id]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => {
      update?.(id, null);
      close.current?.();
    }, 4500);
    return () => clearTimeout(timer);
  }, [toast, update, id]);
  return update ? null : <ToastNotification toast={toast} onClose={onClose} />;
}

function ToastNotification({ toast, onClose }) {
  if (!toast) return null;
  const { tone = 'info', message, title } = toast;
  const Icon = tone === 'success' ? Check : tone === 'warning' ? TriangleAlert : tone === 'error' ? CircleAlert : Info;
  const defaultTitle = { success: 'Success', error: 'Attention needed', warning: 'Warning', info: 'Information' }[tone] || 'Information';
  return <div className={`wh-toast ${tone}`} role="status">
    <span className="wh-toast-icon" aria-hidden="true"><Icon size={20} /></span>
    <div className="wh-toast-copy"><strong>{title || defaultTitle}</strong><span>{message}</span></div>
    <button className="wh-toast-close" type="button" aria-label="Dismiss notification" onClick={onClose}><X size={14} /></button>
  </div>;
}
